import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Shield,
  Bell,
  FileText,
  GitBranch,
  AlertTriangle,
  BookOpen,
  Cpu,
  Radar,
  LayoutDashboard,
  BarChart3,
  LogOut,
  ChevronLeft,
  Menu,
  Archive,
  Settings,
  Users,
  PlugZap,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import { listNotifications, setComplianceContext } from "../../services/compliance-api";

const navItems = [
  { path: "/Compliance/logistics/dashboard", label: "Dashboard", icon: Shield },
  {
    path: "/Compliance/logistics/control-tower",
    label: "Control Tower",
    icon: LayoutDashboard,
  },
  {
    path: "/Compliance/logistics/analytics",
    label: "Analytics",
    icon: BarChart3,
  },
  {
    path: "/Compliance/logistics/v2-intelligence",
    label: "V2 Intelligence",
    icon: Radar,
  },
  {
    path: "/Compliance/logistics/documents",
    label: "Documents",
    icon: FileText,
  },
  {
    path: "/Compliance/logistics/cases",
    label: "Shipment Cases",
    icon: Archive,
  },
  {
    path: "/Compliance/logistics/workflows",
    label: "Workflows",
    icon: GitBranch,
  },
  {
    path: "/Compliance/logistics/risks",
    label: "Risk Center",
    icon: AlertTriangle,
  },
  {
    path: "/Compliance/logistics/audit",
    label: "Audit Center",
    icon: BookOpen,
  },
  { path: "/Compliance/logistics/copilot", label: "Copilot", icon: Cpu },
  { path: "/Compliance/logistics/notifications", label: "Notifications", icon: Bell },
];

const adminNavItems = [
  {
    path: "/Compliance/logistics/workflows/manage",
    label: "Workflow Manager",
    icon: Settings,
  },
  {
    path: "/Compliance/logistics/users",
    label: "Users",
    icon: Users,
  },
  {
    path: "/Compliance/logistics/integrations/webhooks",
    label: "Integrations",
    icon: PlugZap,
  },
];

export default function ComplianceLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { credentials, logout } = useComplianceAuth();
  // Workflow Manager, Users, and Integrations are root-admin only — a
  // tenant's own COMPLIANCE_ADMIN role does not grant this, only the
  // root admin's fixed tenant_admin tenant does.
  const isAdmin = credentials?.tenantId === "tenant_admin" && credentials?.role === "ROOT_ADMIN";
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep the API client's module-level context in sync with the stored credentials
  useEffect(() => {
    if (credentials) {
      setComplianceContext(credentials.tenantId, credentials.role);
    }
  }, [credentials?.tenantId, credentials?.role]);

  // Poll for unread notifications every 30 s
  useEffect(() => {
    if (!credentials) return;
    const fetchCount = () => {
      listNotifications({ unread_only: true })
        .then((r) => setUnreadCount(r.unread_count))
        .catch(() => {});
    };
    fetchCount();
    pollRef.current = setInterval(fetchCount, 30_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [credentials?.tenantId]);

  function handleLogout() {
    logout();
    navigate("/Compliance/logistics/login", { replace: true });
  }

  const roleLabel =
    credentials?.role
      .replace("COMPLIANCE_", "")
      .replace("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase()) ?? "";

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? "w-64" : "w-20"
        } bg-gray-950 text-white transition-all duration-300 flex flex-col border-r border-gray-800`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-blue-400 shrink-0" />
              <div>
                <p className="text-sm font-bold text-white leading-tight">
                  Compliance
                </p>
                <p className="text-xs text-gray-500 leading-tight">
                  LightLink AI
                </p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white ml-auto"
          >
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map(({ path, label, icon: Icon }) => {
            const isActive =
              location.pathname === path ||
              (path === "/compliance/dashboard" &&
                location.pathname === "/compliance");
            const isNotifications = label === "Notifications";
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-sm ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`}
                title={label}
              >
                <span className="relative shrink-0">
                  <Icon size={18} />
                  {isNotifications && unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </span>
                {sidebarOpen && <span>{label}</span>}
                {sidebarOpen && isNotifications && unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                    {unreadCount}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Admin-only section */}
          {isAdmin && (
            <>
              {sidebarOpen && (
                <p className="text-[10px] uppercase tracking-widest text-gray-600 px-3 pt-3 pb-1">
                  Admin
                </p>
              )}
              {adminNavItems.map(({ path, label, icon: Icon }) => {
                const isActive = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-sm ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : "text-gray-400 hover:bg-gray-800 hover:text-white"
                    }`}
                    title={label}
                  >
                    <Icon size={18} className="shrink-0" />
                    {sidebarOpen && <span>{label}</span>}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Back to main platform link */}
        {sidebarOpen && (
          <div className="px-3 pb-2">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-800 transition"
            >
              <ChevronLeft size={14} />
              Main Platform
            </Link>
          </div>
        )}

        {/* User info + logout */}
        <div className="p-4 border-t border-gray-800">
          {sidebarOpen ? (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-white truncate">
                  {credentials?.tenantId}
                </p>
                <p className="text-xs text-gray-400 truncate">{roleLabel}</p>
                {credentials?.mfaVerified && (
                  <span className="text-xs text-green-400">MFA ✓</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                title="Sign out of compliance portal"
                className="text-gray-400 hover:text-red-400 transition-colors shrink-0"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleLogout}
              title="Sign out"
              className="w-full flex justify-center text-gray-400 hover:text-red-400 transition-colors"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 h-full overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}
